import { faMessageCode } from '@fortawesome/pro-light-svg-icons';
import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { ROLES } from '../../../../config/permissions/roles';
import { useJuror } from '../../../../hooks/useAuth';
import { AWARD_TYPES } from '../../../../models/award';
import Submission from '../../../../models/submission';
import Button, { ButtonSizes, ButtonTypes } from '../../../button';
import Dialog from '../../../dialog';
import styles from './styles.module.scss';
import { isAwardType } from '../../../../helpers/awardTypes';
import { SESSION_TYPES } from '../../../../models/session';
import { combine } from '../../../../helpers/styles';

const MAX_CHARS = 280;
const MIN_CHARS = {
  ['DEFAULT']: 1,
  [AWARD_TYPES.IF_DTA]: 15,
};

const CommentModal = ({
  submission,
  onClose,
  onSubmit,
  visible,
  isAdminView,
  onDelete,
}) => {
  const { t } = useTranslation();
  const [comment, setComment] = useState('');
  const [isConfirmationVisible, setIsConfirmationVisible] = useState(false);
  const [hasEdited, setHasEdited] = useState(false);
  const { role, session, juror, jurorGroup, isPreview } = useJuror();

  const canComment =
    role != ROLES.chairPerson ||
    (role == ROLES.chairPerson && submission?.chairPersonMustDecide) ||
    session?.sessionType === SESSION_TYPES.FINAL_JURY;

  const hasMinCharCount = MIN_CHARS[session?.awardType]
    ? MIN_CHARS[session.awardType] <= comment.length
    : MIN_CHARS['DEFAULT'] <= comment.length;

  useEffect(() => {
    if (!visible) return setComment('');
    if (isAdminView) {
      Submission.fetchJuryStatement(
        submission.evaluations[0]?.jurorGroupId,
        submission.submissionId
      ).then((juryStatement) => {
        setComment(juryStatement || '');
      });
    }
    if (isAwardType(AWARD_TYPES.IF_DA, session)) {
      const currentJurorEvaluation = submission?.evaluations?.find(
        (evaluation) =>
          evaluation.jurorId === submission?.jurorId ||
          evaluation.jurorId === juror?.jurorId
      );

      setComment(currentJurorEvaluation?.comment || submission?.comment);
    }
    if (
      isAwardType(AWARD_TYPES.IF_SIP, session) &&
      (submission.suggestedForNomination || submission.hasJuryStatement)
    ) {
      Submission.fetchJuryStatement(
        jurorGroup.jurorGroupId,
        submission.submissionId
      ).then((juryStatement) => {
        setComment(juryStatement || '');
      });
    }

    if (
      isAwardType(AWARD_TYPES.IF_DTA, session) &&
      (submission?.hasJuryStatement || submission?.suggestedForNomination)
    ) {
      Submission.fetchJuryStatement(
        jurorGroup.jurorGroupId,
        submission.submissionId
      ).then((juryStatement) => {
        setComment(juryStatement || '');
      });
    }
  }, [setComment, visible, submission, juror, jurorGroup]);

  const handleSubmit = () => {
    const currentJurorEvaluation = submission?.evaluations?.find(
      (evaluation) =>
        evaluation.jurorId === submission?.jurorId ||
        evaluation.jurorId === juror?.jurorId
    );
    onSubmit({
      comment,
      isNewComment: !(currentJurorEvaluation?.comment || submission?.comment),
    });
  };

  const handleCommentChange = (e) => {
    if (e.target.value.length > MAX_CHARS || isAdminView) return;
    setComment(e.target.value);
    setHasEdited(true);
  };

  if (!visible) return null;

  return (
    <Dialog
      title={t(
        `jurorEntries.table.columns.actions.discuss.dialog.title.${
          session?.awardType || 'IF_SIP'
        }`
      )}
      titleIcon={faMessageCode}
      onClose={onClose}
      noNav
      body={
        <>
          {!isAdminView && !isAwardType(AWARD_TYPES.IF_SIP, session) && (
            <div className={styles.commentListWrapper}>
              {submission.evaluations
                ?.filter((evaluation) => !!evaluation?.comment)
                .map((evaluation) => (
                  <div className={styles.commentWrapper}>
                    <span className={styles.name}>
                      {evaluation?.firstname} {evaluation?.lastname}
                    </span>
                    <span className={styles.comment}>
                      {evaluation?.comment}
                    </span>
                  </div>
                ))}
            </div>
          )}
          {canComment && (
            <>
              <textarea
                rows={4}
                value={comment}
                onChange={handleCommentChange}
                className={styles.textarea}
                readOnly={isPreview}
                disabled={isConfirmationVisible}
              />

              <div
                className={combine(
                  styles.charsContainer,
                  isAwardType(AWARD_TYPES.IF_DTA, session) &&
                    hasEdited &&
                    !hasMinCharCount &&
                    styles.invalid
                )}
              >
                {isAwardType(AWARD_TYPES.IF_DTA, session) ? (
                  <span>
                    {t(
                      'jurorEntries.table.columns.actions.discuss.dialog.hintMinMaxChars'
                    )}
                  </span>
                ) : (
                  <span>
                    {t(
                      'jurorEntries.table.columns.actions.discuss.dialog.hintMaxChars'
                    )}
                  </span>
                )}
                <span className={'d-flex justify-content-end'}>{`${
                  comment?.length || 0
                } / 280`}</span>
              </div>
            </>
          )}
          {isConfirmationVisible && (
            <div className={styles.confirmationContainer}>
              <p className={styles.message}>
                {t(
                  'jurorEntries.table.columns.actions.discuss.dialog.deleteConfirmation'
                )}
              </p>
              <div className={styles.buttonRow}>
                <Button
                  text={t(
                    'jurorEntries.table.columns.actions.discuss.dialog.buttons.close'
                  )}
                  size={ButtonSizes.LARGE}
                  type={ButtonTypes.SECONDARY}
                  onClick={() => {
                    setIsConfirmationVisible(false);
                  }}
                />
                <Button
                  text={t(
                    'jurorEntries.table.columns.actions.discuss.dialog.buttons.delete'
                  )}
                  type={ButtonTypes.TERTIARY}
                  size={ButtonSizes.LARGE}
                  onClick={() => {
                    onDelete();
                    setIsConfirmationVisible(false);
                  }}
                />
              </div>
            </div>
          )}
        </>
      }
      footer={
        !isConfirmationVisible && (
          <>
            <div
              className={combine(
                styles.modalFooter,
                isAwardType(AWARD_TYPES.IF_DTA, session) && styles.spaceBetween
              )}
            >
              {isAwardType(AWARD_TYPES.IF_DTA, session) && (
                <div>
                  <Button
                    text={t(
                      'jurorEntries.table.columns.actions.discuss.dialog.buttons.delete'
                    )}
                    type={ButtonTypes.TERTIARY}
                    size={ButtonSizes.LARGE}
                    onClick={() => {
                      setIsConfirmationVisible(true);
                    }}
                    disabled={!submission.suggestedForNomination}
                  />
                </div>
              )}
              <div className={styles.rightButtonGroup}>
                {canComment && (
                  <>
                    <Button
                      text={t(
                        'jurorEntries.table.columns.actions.discuss.dialog.buttons.cancel'
                      )}
                      type={ButtonTypes.SECONDARY}
                      size={ButtonSizes.LARGE}
                      onClick={onClose}
                    />
                    <Button
                      text={t(
                        'jurorEntries.table.columns.actions.discuss.dialog.buttons.save'
                      )}
                      disabled={!hasMinCharCount || isAdminView || isPreview}
                      type={ButtonTypes.PRIMARY}
                      size={ButtonSizes.LARGE}
                      onClick={handleSubmit}
                    />
                  </>
                )}
                {!canComment && (
                  <Button
                    text={t(
                      'jurorEntries.table.columns.actions.discuss.dialog.buttons.close'
                    )}
                    type={ButtonTypes.PRIMARY}
                    size={ButtonSizes.LARGE}
                    onClick={onClose}
                  />
                )}
              </div>
            </div>
          </>
        )
      }
    />
  );
};

CommentModal.propTypes = {
  submission: PropTypes.object,
  onClose: PropTypes.func,
  onSubmit: PropTypes.func,
  visible: PropTypes.bool,
  isAdminView: PropTypes.bool,
  onDelete: PropTypes.func,
};

CommentModal.defaultProps = {
  submission: null,
  onClose: () => null,
  onSubmit: () => null,
  visible: false,
  isAdminView: false,
  onDelete: () => null,
};

export default CommentModal;
